import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import AuthContext from '../contexts/AuthContext';
import { StrictModeDroppable } from '../components/board/StrictModeDroppable';
import CardModal from '../components/cards/CardModal';
import CardPicker from '../components/dailyplan/CardPicker';
import Pomodoro from '../components/dailyplan/Pomodoro';

// --- date helpers (local time) ---
const toKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const addDays = (base, n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const buildTimes = (startHour, endHour) => {
  const out = [];
  for (let h = startHour; h < endHour; h++) {
    out.push(`${String(h).padStart(2, '0')}:00`);
    out.push(`${String(h).padStart(2, '0')}:30`);
  }
  return out;
};
const label12 = (t) => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
};

const START_HOUR = 7;
const END_HOUR = 19;

// Stable per-row id so drag-and-drop can track content as it moves between time slots.
let _uid = 0;
const uid = () => `row-${++_uid}`;
const emptyRow = () => ({ uid: uid(), type: 'free', card: null, label: '', worked: false, notes: '' });
const makeRow = (slot) => ({ uid: uid(), type: slot.type, card: slot.card || null, label: slot.label || '', worked: !!slot.worked, notes: slot.notes || '' });

// Move a row's content to a new slot (immutable, length-stable — rows stay aligned to `times`).
// - Destination empty: the content drops straight into it and the source becomes empty. Nothing
//   else shifts (so dragging back onto an earlier empty slot just fills it in place).
// - Destination occupied: insert there and push the occupant plus everything after it FORWARD
//   (to later times) until the next empty slot absorbs the shift. No content is lost or reordered.
const moveRow = (rows, from, to) => {
  if (from === to) return rows;
  const next = rows.slice();
  const moving = next[from];
  next[from] = emptyRow();                       // vacate the source slot
  if (next[to].type === 'free') {                // empty destination: replace it, no ripple
    next[to] = moving;
    return next;
  }
  // Occupied: find the first empty slot at or after the destination to absorb the ripple forward.
  const gap = next.findIndex((r, i) => i >= to && r.type === 'free');
  if (gap === -1) return rows;                    // no room ahead — refuse rather than drop a task
  for (let i = gap; i > to; i--) next[i] = next[i - 1];
  next[to] = moving;
  return next;
};

const slotMinutes = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

// Minutes-of-day an imported event covers on a given local day (clamped to that day).
const eventSpan = (e, dayStart) => {
  const dayEnd = addDays(dayStart, 1);
  const s = new Date(e.start);
  const en = new Date(e.end);
  const toMin = (d) => (d <= dayStart ? 0 : d >= dayEnd ? 24 * 60 : d.getHours() * 60 + d.getMinutes());
  return { from: toMin(s), to: toMin(en) };
};
const blocksTime = (e) => e.blocksDayPlan && e.busy !== 'free';

const DailyPlan = () => {
  const { token } = useContext(AuthContext);
  const today = useMemo(() => new Date(), []);
  const times = useMemo(() => buildTimes(START_HOUR, END_HOUR), []);
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);            // index-aligned to `times`
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('saved');
  const [pickerUid, setPickerUid] = useState(null);
  const [openCardId, setOpenCardId] = useState(null);
  const [selectedUid, setSelectedUid] = useState(null);
  const [notesUid, setNotesUid] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [dayEvents, setDayEvents] = useState([]);   // imported calendar events (Outlook .ics etc.)
  const saveTimer = useRef(null);

  const dateObj = addDays(today, offset);
  const dateKey = toKey(dateObj);

  // Imported calendar events for this day.
  useEffect(() => {
    let active = true;
    const start = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    const end = addDays(start, 1);
    axios.get('/api/calendar/events', {
      params: { from: new Date(start.getTime() - 864e5).toISOString(), to: end.toISOString() },
      headers: { 'x-auth-token': token }
    })
      .then((res) => {
        if (!active) return;
        setDayEvents((res.data || []).filter((e) => (e.allDay
          ? e.startDay <= dateKey && dateKey < e.endDay
          : new Date(e.start) < end && new Date(e.end) > start)));
      })
      .catch(() => { if (active) setDayEvents([]); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey, token]);

  const dayStart = useMemo(() => new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateKey]);

  // Per-slot timed events, plus the ones that belong in the banner (all-day or outside the grid window).
  const { slotEvents, bannerEvents } = useMemo(() => {
    const perSlot = times.map(() => []);
    const banner = [];
    const gridFrom = START_HOUR * 60;
    const gridTo = END_HOUR * 60;
    dayEvents.forEach((e) => {
      if (e.allDay) { banner.push(e); return; }
      const { from, to } = eventSpan(e, dayStart);
      if (to <= gridFrom || from >= gridTo) { banner.push(e); return; }
      times.forEach((t, i) => {
        const s0 = slotMinutes(t);
        if (from < s0 + 30 && (to > s0 || (to === from && from >= s0))) perSlot[i].push({ ...e, startsHere: (from >= s0 && from < s0 + 30) || (i === 0 && from < s0) });
      });
    });
    return { slotEvents: perSlot, bannerEvents: banner };
  }, [dayEvents, dayStart, times]);

  const slotBlocked = (i) => (slotEvents[i] || []).some(blocksTime);

  // Load a day → ordered rows aligned to the fixed time grid (gaps = empty rows).
  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`/api/dayplan/${dateKey}`, { headers: { 'x-auth-token': token } });
        if (!active) return;
        const map = {};
        (res.data.slots || []).forEach((s) => { map[s.time] = s; });
        setRows(times.map((t) => (map[t] ? makeRow(map[t]) : emptyRow())));
      } catch (e) {
        if (active) setRows(times.map(() => emptyRow()));
      } finally {
        if (active) { setLoading(false); setSaveState('saved'); }
      }
    })();
    return () => { active = false; };
  }, [dateKey, token, times]);

  const serialize = useCallback((rs) => rs.map((r, i) => ({
    time: times[i],
    type: r.type,
    card: r.type === 'task' ? (r.card?._id || r.card || null) : null,
    label: r.label || '',
    worked: !!r.worked,
    notes: r.notes || ''
  })), [times]);

  // Debounced auto-save; merges re-populated card objects back by index.
  const scheduleSave = useCallback((rs) => {
    clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await axios.put(`/api/dayplan/${dateKey}`,
          { slots: serialize(rs), startHour: START_HOUR, endHour: END_HOUR },
          { headers: { 'x-auth-token': token } });
        const byTime = {};
        (res.data.slots || []).forEach((s) => { byTime[s.time] = s; });
        setRows((cur) => cur.map((r, i) => {
          const fresh = byTime[times[i]];
          return (r.type === 'task' && fresh && fresh.card) ? { ...r, card: fresh.card } : r;
        }));
        setSaveState('saved');
      } catch (e) {
        setSaveState('saved');
      }
    }, 700);
  }, [dateKey, token, serialize, times]);

  const patchRow = useCallback((rowUid, patch) => {
    setRows((cur) => {
      const next = cur.map((r) => (r.uid === rowUid ? { ...r, ...patch } : r));
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  // Clearing keeps the slot in place but empties it (a fresh empty row).
  const clearRow = (rowUid) => {
    setRows((cur) => {
      const next = cur.map((r) => (r.uid === rowUid ? emptyRow() : r));
      scheduleSave(next);
      return next;
    });
    if (selectedUid === rowUid) setSelectedUid(null);
  };

  const handlePick = (card) => {
    if (pickerUid) patchRow(pickerUid, { type: 'task', card, label: '', worked: false });
    setPickerUid(null);
  };

  // Drag a slot's content to a new position; occupied targets ripple forward, empty targets fill in place.
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    setRows((cur) => {
      const next = moveRow(cur, source.index, destination.index);
      if (next === cur) return cur;
      scheduleSave(next);
      return next;
    });
  };

  // On today, time slots earlier than "now" are in the past and can't accept a new task/appointment.
  const isPastSlot = (i) => {
    if (offset !== 0) return false;
    const now = new Date();
    return slotMinutes(times[i]) < now.getHours() * 60 + now.getMinutes();
  };

  // --- Plan my Day (Jim Brain) ---
  const planMyDay = async () => {
    setPlanning(true);
    try {
      const freeSlots = rows.map((r, i) => (r.type === 'free' && !slotBlocked(i) ? times[i] : null)).filter(Boolean);
      const placedCardIds = rows.filter((r) => r.type === 'task' && (r.card?._id || r.card)).map((r) => r.card?._id || r.card);
      const res = await axios.post(`/api/dayplan/${dateKey}/plan`,
        { freeSlots, placedCardIds },
        { headers: { 'x-auth-token': token } });
      setSuggestions(res.data.suggestions || []);
    } catch (e) {
      setSuggestions({ error: e.response?.data?.msg || 'Could not reach Jim Brain. Is the workstation up?' });
    } finally {
      setPlanning(false);
    }
  };

  const applySuggestions = () => {
    if (!Array.isArray(suggestions) || suggestions.length === 0) { setSuggestions(null); return; }
    setRows((cur) => {
      const next = cur.slice();
      suggestions.forEach((s) => {
        const idx = times.indexOf(s.time);
        if (idx >= 0) next[idx] = makeRow({ type: 'task', card: s.cardId });
      });
      scheduleSave(next);
      return next;
    });
    setSuggestions(null);
  };

  const focusTask = useMemo(() => {
    const r = rows.find((x) => x.uid === selectedUid);
    if (!r) return null;
    if (r.type === 'task') return r.card?.title || 'Task';
    if (r.type === 'appointment') return r.label;
    return null;
  }, [selectedUid, rows]);

  const taskCount = rows.filter((r) => r.type === 'task').length;
  const workedCount = rows.filter((r) => r.type === 'task' && r.worked).length;
  const meetingCount = dayEvents.filter((e) => !e.allDay).length;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-gray-900 dark:text-gray-100 uppercase">Daily Plan</h1>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">
            {taskCount > 0 ? `${workedCount}/${taskCount} tasks worked` : 'Block your day in 30-minute chunks'}
            {meetingCount > 0 && ` · ${meetingCount} meeting${meetingCount === 1 ? '' : 's'}`}
            <span className="ml-2 normal-case tracking-normal text-gray-300">· {saveState === 'saving' ? 'Saving…' : 'Saved'}</span>
          </p>
        </div>
        <button
          onClick={planMyDay}
          disabled={planning}
          className="px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-xs text-white bg-primary-blue hover:opacity-90 disabled:opacity-50 transition-all">
          {planning ? 'Jim is thinking…' : '✨ Plan my Day'}
        </button>
      </div>

      {/* 7-day selector */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
        {Array.from({ length: 7 }).map((_, i) => {
          const d = addDays(today, i);
          const active = i === offset;
          return (
            <button
              key={i}
              onClick={() => setOffset(i)}
              className={`flex-shrink-0 w-16 py-2 rounded-xl text-center transition-all border ${active
                ? 'bg-primary-blue text-white border-primary-blue shadow'
                : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-100 dark:border-gray-800 hover:border-primary-blue/40'}`}>
              <div className="text-[10px] font-black uppercase tracking-widest opacity-80">{i === 0 ? 'Today' : DOW[d.getDay()]}</div>
              <div className="text-lg font-black leading-none mt-0.5">{d.getDate()}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Planning grid */}
        <div className="lg:col-span-2">
          {bannerEvents.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {bannerEvents.map((e) => (
                <span
                  key={e._id}
                  title={`${e.calendarName}${e.location ? ` · ${e.location}` : ''}`}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold text-gray-700 dark:text-gray-200"
                  style={{ borderLeft: `3px solid ${e.color}`, background: `${e.color}1a` }}>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">
                    {e.allDay ? 'All day' : new Date(e.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                  {e.title}
                </span>
              ))}
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-blue" />
            </div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <StrictModeDroppable droppableId="dayplan">
                {(dropProvided) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                    {rows.map((r, i) => (
                      <Draggable key={r.uid} draggableId={r.uid} index={i}>
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            onClick={() => setSelectedUid(r.uid)}
                            className={`flex items-stretch border-b border-gray-50 dark:border-gray-800 last:border-0 cursor-pointer transition-colors ${snapshot.isDragging ? 'bg-white dark:bg-gray-800 shadow-lg rounded-xl' : selectedUid === r.uid ? 'bg-primary-blue/5' : 'hover:bg-gray-50/60 dark:hover:bg-gray-800/40'}`}>
                            {/* Time gutter = drag handle */}
                            <div
                              {...dragProvided.dragHandleProps}
                              className="w-24 flex-shrink-0 px-2 py-3 flex items-center justify-end gap-1.5 text-right cursor-grab active:cursor-grabbing select-none">
                              <svg className="h-3.5 w-3.5 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20"><path d="M7 4a1 1 0 110 2 1 1 0 010-2zm6 0a1 1 0 110 2 1 1 0 010-2zM7 9a1 1 0 110 2 1 1 0 010-2zm6 0a1 1 0 110 2 1 1 0 010-2zM7 14a1 1 0 110 2 1 1 0 010-2zm6 0a1 1 0 110 2 1 1 0 010-2z" /></svg>
                              <span className="text-[11px] font-bold text-gray-400 tabular-nums">{label12(times[i])}</span>
                            </div>
                            {/* Content */}
                            <div className="flex-1 min-w-0 py-2 pr-3">
                              {(slotEvents[i] || []).map((e) => (
                                <div
                                  key={e._id}
                                  title={`${e.calendarName}${e.location ? ` · ${e.location}` : ''}`}
                                  className={`flex items-center gap-2 rounded-md px-2 py-1 text-xs font-semibold truncate ${r.type === 'free' ? '' : 'mb-1.5'} ${blocksTime(e) ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400'}`}
                                  style={{ borderLeft: `3px solid ${e.color}`, background: `${e.color}${blocksTime(e) ? '1f' : '0d'}` }}>
                                  <span className="truncate">{e.startsHere ? e.title : `↳ ${e.title}`}</span>
                                  {e.startsHere && (
                                    <span className="flex-shrink-0 text-[10px] font-bold text-gray-400 tabular-nums">
                                      {new Date(e.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} to {new Date(e.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                      {e.busy === 'tentative' && ' · tentative'}
                                    </span>
                                  )}
                                </div>
                              ))}
                              {r.type === 'free' && !slotBlocked(i) && (
                                isPastSlot(i) ? (
                                  <div className="py-2 text-[11px] font-bold uppercase tracking-widest text-gray-300 dark:text-gray-700 select-none">Past</div>
                                ) : (
                                  <div className="flex gap-2 py-1" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={() => setPickerUid(r.uid)}
                                      className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-primary-blue border border-dashed border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 transition-colors">
                                      + Task
                                    </button>
                                    <button
                                      onClick={() => patchRow(r.uid, { type: 'appointment', label: '' })}
                                      className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-primary-blue border border-dashed border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 transition-colors">
                                      + Appointment
                                    </button>
                                  </div>
                                )
                              )}

                              {r.type === 'task' && (
                                <div className="flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={r.worked}
                                    onChange={(e) => patchRow(r.uid, { worked: e.target.checked })}
                                    title="Mark as worked"
                                    className="mt-1 h-4 w-4 rounded accent-primary-blue cursor-pointer" />
                                  <div className="flex-1 min-w-0">
                                    <button
                                      onClick={() => setOpenCardId(r.card?._id || r.card)}
                                      className={`text-left text-sm font-semibold hover:text-primary-blue truncate block w-full ${r.worked ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>
                                      {r.card?.title || 'Open task…'}
                                    </button>
                                    {r.card?.board?.name && (
                                      <span className="text-[10px] text-gray-400 uppercase tracking-widest">{r.card.board.name}</span>
                                    )}
                                  </div>
                                  <SlotActions rowUid={r.uid} notesUid={notesUid} setNotesUid={setNotesUid} clearRow={clearRow} />
                                </div>
                              )}

                              {r.type === 'appointment' && (
                                <div className="flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={r.worked}
                                    onChange={(e) => patchRow(r.uid, { worked: e.target.checked })}
                                    className="mt-1 h-4 w-4 rounded accent-emerald-500 cursor-pointer" />
                                  <input
                                    autoFocus={!r.label}
                                    value={r.label}
                                    onChange={(e) => patchRow(r.uid, { label: e.target.value })}
                                    placeholder="Meeting / appointment…"
                                    className={`flex-1 min-w-0 bg-transparent text-sm font-semibold focus:outline-none border-b border-transparent focus:border-primary-blue/40 ${r.worked ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`} />
                                  <SlotActions rowUid={r.uid} notesUid={notesUid} setNotesUid={setNotesUid} clearRow={clearRow} />
                                </div>
                              )}

                              {notesUid === r.uid && r.type !== 'free' && (
                                <textarea
                                  onClick={(e) => e.stopPropagation()}
                                  value={r.notes}
                                  onChange={(e) => patchRow(r.uid, { notes: e.target.value })}
                                  placeholder="Quick notes for this block…"
                                  rows={2}
                                  className="mt-2 w-full text-xs bg-gray-50 dark:bg-gray-800 rounded-lg p-2 border border-gray-100 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-blue/40" />
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          )}
        </div>

        {/* Pomodoro */}
        <div className="lg:col-span-1">
          <Pomodoro focusTask={focusTask} />
        </div>
      </div>

      {pickerUid && <CardPicker onPick={handlePick} onClose={() => setPickerUid(null)} />}

      {openCardId && (
        <CardModal
          cardId={openCardId}
          onClose={() => setOpenCardId(null)}
          onUpdate={() => scheduleSave(rows)}
        />
      )}

      {suggestions !== null && (
        <SuggestionModal suggestions={suggestions} label12={label12} onApply={applySuggestions} onClose={() => setSuggestions(null)} />
      )}
    </div>
  );
};

const SlotActions = ({ rowUid, notesUid, setNotesUid, clearRow }) => (
  <div className="flex items-center gap-1 flex-shrink-0">
    <button
      onClick={() => setNotesUid(notesUid === rowUid ? null : rowUid)}
      title="Notes"
      className="p-1 text-gray-300 hover:text-primary-blue transition-colors">
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    </button>
    <button
      onClick={() => clearRow(rowUid)}
      title="Clear slot"
      className="p-1 text-gray-300 hover:text-red-500 transition-colors">
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const SuggestionModal = ({ suggestions, label12, onApply, onClose }) => {
  const error = !Array.isArray(suggestions) && suggestions?.error;
  const empty = Array.isArray(suggestions) && suggestions.length === 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-sm font-black uppercase tracking-widest text-gray-800 dark:text-gray-100">Jim's suggested plan</h3>
        </div>
        <div className="max-h-80 overflow-y-auto px-5 py-3">
          {error && <p className="text-sm text-red-500 py-4">{error}</p>}
          {empty && <p className="text-sm text-gray-400 py-4">No tasks to schedule right now. Your open tasks may already be placed or complete.</p>}
          {Array.isArray(suggestions) && suggestions.map((s, i) => (
            <div key={i} className="py-2.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-black text-primary-blue tabular-nums w-16 flex-shrink-0">{label12(s.time)}</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{s.reason || 'Scheduled'}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600">Cancel</button>
          {Array.isArray(suggestions) && suggestions.length > 0 && (
            <button onClick={onApply} className="px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-primary-blue hover:opacity-90">Apply to grid</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyPlan;
